import {z} from 'zod';
import {convertProductUnit,resolveUnitPrice} from './unitPricing';
import {requisitionDate} from './requisitions';
const key=(v:string)=>v.trim().toUpperCase();
const money4=/^(?:0|[1-9]\d{0,11})(?:\.\d{1,4})?$/;
const money2=/^(?:0|[1-9]\d{0,11})(?:\.\d{1,2})?$/;
/** Integer minor units (4dp) from a decimal string. No float arithmetic. */
export function amountToMinor(value:string,dp=4):number{
 if(!(dp===2?money2:money4).test(value.trim()))throw Error('document_price');
 const [w,f='']=value.trim().split('.');
 const n=Number(w)*10000+Number(f.padEnd(4,'0').slice(0,4));
 if(!Number.isSafeInteger(n))throw Error('document_price');
 return n;
}
export function minorToAmount(minor:number,dp=4):string{
 const sign=minor<0?'-':'';const a=Math.abs(Math.trunc(minor));const w=Math.floor(a/10000);const frac=String(a%10000).padStart(4,'0').slice(0,dp).replace(/0+$/,'');
 return sign+String(w)+(frac?'.'+frac:'');
}
export type PriceChoice='negotiated'|'profile'|'unknown';
export interface UnitProfileRules {baseUnit:string;units:{unit:string;alternateQuantity:string;baseQuantity:string}[];prices:{unit:string;currency:string;amount:string}[];}
export interface LinePriceInput {
 lineId:string;productId:string;documentUnit:string;documentQuantity:string;currency:string;
 negotiatedMinor:number;productBaseUnit:string;productExwMinor:number|null;productDodMinor:number|null;productUnderMoqMinor:number|null;
 profile:{id:string;version:string;definition:UnitProfileRules}|null;
}
/**
 * Lần quy đổi tiền tệ đã áp vào đơn giá của dòng (yêu cầu v1.8 mục 6.4, 11/10/2026): tiền gốc, đơn giá gốc, tỷ giá đã dùng (có chiều)
 * và kết quả. Ghi một lần lúc lưu; đổi tỷ giá trong Cài đặt về sau không tính lại. Sửa tay đơn giá thì bản ghi này rơi khỏi dòng.
 */
export interface LineFxSnapshot {
 sourceCurrency:string;sourceUnitPrice:string;targetCurrency:string;
 base:string;quote:string;rate:string;convertedUnitPrice:string;appliedAt:number;
}
export interface LinePriceSnapshot {
 selection?:import('./schemas').UnitSelection;
 fx?:LineFxSnapshot;
 lineId:string;documentUnit:string;baseUnit:string;documentQuantity:string;quantityInBase:string|null;conversionRounded:boolean;
 productId?:string|null;currency?:string;conversionStatus?:'known'|'missing';
 sourceDocumentId?:string;sourceLineId?:string;
 conversionRule?:{alternateQuantity:string;baseQuantity:string};
 profileId:string|null;profileVersion:string|null;
 negotiated:string;profilePrice:string|null;exw:string|null;dod:string|null;underMoq:string|null;
 chosen:string|null;choice:PriceChoice;explanation:string;
}
/** Reuse only evidence captured on the source, never today's profile. */
export function convertCapturedQuantity(snapshot:LinePriceSnapshot,quantity:string,targetUnit:string,rounding:'halfUp'|'up'|'down'='halfUp'):string|null{
 if(snapshot.quantityInBase===null||key(snapshot.baseUnit)!==key(targetUnit))return null;
 if(key(snapshot.documentUnit)===key(snapshot.baseUnit))return quantity;
 const rule=snapshot.conversionRule??(snapshot.documentQuantity!=='0'?{alternateQuantity:snapshot.documentQuantity,baseQuantity:snapshot.quantityInBase}:null);
 if(!rule)return null;
 try{return convertProductUnit({baseUnit:snapshot.baseUnit,units:[{unit:snapshot.documentUnit,...rule}],prices:[]},quantity,snapshot.documentUnit,targetUnit,rounding).quantity;}catch{return null;}
}
/**
 * Snapshot for a NEW commercial line. Priority (not a customer-mandated global standard):
 * 1. Negotiated price already on the source document (PI line).
 * 2. Independent unit/currency price from the active product profile.
 * 3. Unknown — never invent FX, never treat EXW/DTD/Under-MOQ as the chosen price.
 * Conversion uses a declared profile; same-unit lines need no profile. Missing conversion throws `document_unit`.
 */
export function snapshotDocumentLine(input:LinePriceInput):LinePriceSnapshot{
 const base=input.productBaseUnit.trim();if(!base)throw Error('document_unit');
 const same=key(input.documentUnit)===key(base);
 let quantityInBase=input.documentQuantity,conversionRounded=false;
 if(input.profile&&key(input.profile.definition.baseUnit)!==key(base))throw Error('document_unit');
 if(!same){if(!input.profile)throw Error('document_unit');try{const c=convertProductUnit(input.profile.definition,input.documentQuantity,input.documentUnit,base);quantityInBase=c.quantity;conversionRounded=c.rounded;}catch{throw Error('document_unit');}}
 const profilePrice=input.profile?resolveUnitPrice(input.profile.definition,input.documentUnit,input.currency):null;
 const negotiated=minorToAmount(input.negotiatedMinor);
 const choice:PriceChoice='negotiated';
 const explanation=input.profile
  ?`Negotiated source-document price is used. Profile ${input.profile.version} price for ${input.documentUnit}/${input.currency} is ${profilePrice??'not declared'}. EXW/DTD/Under-MOQ are snapshotted and not chosen.`
  :`Negotiated source-document price is used. No active unit profile. EXW/DTD/Under-MOQ are snapshotted and not chosen.`;
 return {
  lineId:input.lineId,documentUnit:input.documentUnit,baseUnit:base,documentQuantity:input.documentQuantity,quantityInBase,conversionRounded,
  productId:input.productId,currency:input.currency,conversionStatus:'known',
  conversionRule:same?{alternateQuantity:'1',baseQuantity:'1'}:input.profile?.definition.units.find(u=>key(u.unit)===key(input.documentUnit)),
  profileId:input.profile?.id??null,profileVersion:input.profile?.version??null,
  negotiated,profilePrice,exw:input.productExwMinor==null?null:minorToAmount(input.productExwMinor),dod:input.productDodMinor==null?null:minorToAmount(input.productDodMinor),underMoq:input.productUnderMoqMinor==null?null:minorToAmount(input.productUnderMoqMinor),
  chosen:negotiated,choice,explanation,
 };
}
export const instalmentInputSchema=z.object({id:z.string().uuid(),dueDate:requisitionDate,amount:z.string().trim().regex(money2)});
export type InstalmentInput=z.infer<typeof instalmentInputSchema>;
/** Empty schedule = not declared. A declared schedule must sum exactly to the document total (2dp). */
export function validateInstalments(totalMinor:number,rows:InstalmentInput[]):void{
 if(!rows?.length)return;
 if(new Set(rows.map(r=>r.id)).size!==rows.length||new Set(rows.map(r=>r.dueDate)).size!==rows.length)throw Error('so_instalment');
 const sum=rows.reduce((n,r)=>n+amountToMinor(r.amount,2),0);
 if(sum!==totalMinor)throw Error('so_instalment');
}
export function creditExposureOk(limitMinor:number|null,openMinor:number,addingMinor:number):boolean{
 if(limitMinor==null)return true;
 return openMinor+addingMinor<=limitMinor;
}
